/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

// Importing necessary modules and interfaces from various packages
import {
    IBuyboxAddToCartViewProps,
    IBuyboxAddToOrderTemplateViewProps,
    IBuyboxAddToWishlistViewProps,
    IBuyboxCallbacks,
    IBuyboxData,
    IBuyboxKeyInPriceViewProps,
    IBuyboxProductConfigureDropdownViewProps,
    IBuyboxProductConfigureViewProps,
    IBuyboxProductQuantityViewProps,
    IBuyboxProps,
    IBuyboxState,
    IBuyboxViewProps
} from '@msdyn365-commerce-modules/buybox';
import { IProductSpecificationViewProps } from '@msdyn365-commerce-modules/product-specification';
import {
    IProductSpecificationProps as IProductSpecificationExtensionProps,
    IProductSpecificationConfig as IProductSpecificationExtensionConfig,
    IReassuranceItemsData as IReassuranceItemsExtensionData
} from '../definition-extensions/product-specification.ext.props.autogenerated';
// NOTE Commented the "Add to MyVenchiMix" button on PDP to facilitate A-B tests on the page
// NOTE ref. https://dev.azure.com/venchi/Venchi.DigitalProducts/_workitems/edit/9624
// NOTE uncomment whenever requested
// import { getCategoryPathsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import * as React from 'react';
import { IBuyboxResources } from '@msdyn365-commerce-modules/buybox/dist/types/modules/buybox/buybox.props.autogenerated';
import { ITelemetryContent, Module, Node, format } from '@msdyn365-commerce-modules/utilities';
// NOTE Commented the "Add to MyVenchiMix" button on PDP to facilitate A-B tests on the page
// NOTE ref. https://dev.azure.com/venchi/Venchi.DigitalProducts/_workitems/edit/9624
// NOTE uncomment whenever requested
// import { getCategoriesUrlSync, Image, IImageData } from '@msdyn365-commerce/core';
import { Image, IImageData } from '@msdyn365-commerce/core';
import { ProductDimension } from '@msdyn365-commerce/retail-proxy';
import { insertBackInStock_CAPAsync_v2 } from '../../../actions/DataActionExtension.g';
import {
    IBuyboxProps as IBuyboxExtensionProps,
    IBuyboxResources as IBuyboxExtensionResources,
    IBuyboxConfig as IBuyboxExtensionConfig,
    IBadgeItemsData as IBadgeItemsExtensionData,
    BadgeItemsBadgeLabelColor as BadgeItemsBadgeLabelColorExtension
} from '../definition-extensions/buybox.ext.props.autogenerated';
import { Quantity } from '../utilities/quantity';
import axios from 'axios';
import { getCustomDiscountPercentage } from '../utils/getCustomDiscountPercentage';
import { trackAddToCartFromPDP, trackAddToWishlistFromPDP, trackViewItemFromPDP } from '../utilities/datalayer/index';
import { getSiteLanguageConfigFromContext, determineSiteLanguageForAbandonedCart } from '../utils/determineSiteLanguageForAbandonedCart';

// Defining interfaces for product specification and badge show types
interface productSpecificationData {
    Name: string;
    BooleanValue: boolean;
    TextValue: string;
    IntegerValue: number;
}

interface BadgeShowType {
    key: string;
    label: string;
    image: IImageData;
    color?: BadgeItemsBadgeLabelColorExtension;
}

interface NutritionalData {
    label: string;
    qty1Label: string;
    qty1Unit: string;
    qty1Value: number;
    qty2Unit: string;
    qty2Value: number;
    subcategory: NutritionalSubcategoryData[];
}

interface NutritionalSubcategoryData {
    label: string;
    qty1Unit: string;
    qty1Value: number;
}

interface ProductLabel {
    label: string;
    imgLink: string;
}

interface ReassuranceShowType {
    title: string;
    description: string;
    image: IImageData;
}

// Certification values enum
enum CertificationValueEnum {
    RainforestAlliance = 'RAMB'
}

// Certification values enum
enum FinishedProductPackagingEnum {
    EasterMiniEggs = 'EASTER MINI EGGS',
    NotRelevant = 'NOT RELEVANT',
    WrappedChocolate = 'WRAPPED CHOCOLATE'
}

interface ProductDescriptionHTMLProps {
    description: string;
}

const ProductDescriptionHTML: React.FC<ProductDescriptionHTMLProps> = ({ description }) => {
    return <span dangerouslySetInnerHTML={{ __html: description }} />;
};

// Main BuyboxView component
const BuyboxView: React.FC<IBuyboxViewProps &
    IBuyboxExtensionProps<IBuyboxData> &
    IBuyboxExtensionConfig &
    IProductSpecificationViewProps &
    IProductSpecificationExtensionProps<IProductSpecificationViewProps> &
    IProductSpecificationExtensionConfig> = props => {
    const {
        ModuleProps,
        MediaGalleryContainerProps,
        ProductInfoContainerProps,
        addToCart,
        addToOrderTemplate,
        addToWishlist,
        telemetryContent,
        configure,
        quantity,
        title,
        callbacks,
        state,
        resources,
        inventoryLabel,
        keyInPrice,
        quantityLimitsMessages,
        max,
        config
    } = props;

    const {
        // Extracting product availability and specification data from props
        // prettier-ignore
        // @ts-ignore
        data: { productAvailableQuantity: { result: productAvailableQuantity }, productSpecificationData: { result: productSpecificationData }
            }
    } = props;
    const availibilityCode = productAvailableQuantity ? productAvailableQuantity[0]?.StockLevelCode : '';

    // TODO: remove this after development - Debug availability code
    React.useEffect(() => {
        console.log('*** availability code ***');
        console.log({ availibilityCode: availibilityCode });
        console.log({ productAvailableQuantity: productAvailableQuantity });
    }, [availibilityCode]);

    // Mapping badge items from configuration
    // const badgesItems: BadgeShowType[] | undefined = props?.config?.badgeItems?.map((badge: IBadgeItemsExtensionData) => ({
    //     key: badge.badgeName,
    //     label: badge.badgeLabel,
    //     color: badge.badgeLabelColor,
    //     image: badge.badgeImage
    // }));
    // const badgesTag = badgesItems?.map(({ key }): string => key);
    const maxBadges = props?.config?.badgeMaxItems !== undefined ? Number(props.config.badgeMaxItems.replace('max_', '')) : 99;

    // Filtering badges to show based on product specification data
    const [badgesItemsState] = React.useState<BadgeShowType[] | undefined>(
        props?.config?.badgeItems?.map((badge: IBadgeItemsExtensionData) => ({
            key: badge.badgeName,
            label: badge.badgeLabel,
            color: badge.badgeLabelColor,
            image: badge.badgeImage
        }))
    );
    const [badgesTagState] = React.useState<string[] | undefined>(badgesItemsState?.map(({ key }): string => key));
    const [badgesToShowState] = React.useState<string[]>([]);

    productSpecificationData?.map((row: productSpecificationData) => {
        const attributeName = (row.Name ?? '').replace('\n', '').trim();
        // if the attribute exists, has the name of a product tag and its value is true, add it specs to the badges to display
        if (
            !badgesToShowState.includes(attributeName) &&
            row !== null &&
            badgesTagState?.includes(attributeName) &&
            (row.BooleanValue === true ||
                (Object.values(CertificationValueEnum) as string[]).includes(row.TextValue) ||
                row.TextValue === 'Yes')
        ) {
            badgesToShowState.push(attributeName);
        }
    });

    // @ts-ignore
    const productSpecificationResult = props?.data?.productSpecificationData?.result;
    const specificationsList = ['Product description', 'Nutritional Values JSON'];
    const specificationsListOrder = { 'Product description': 1, 'Nutritional Values JSON': 2, Reassurance: 3, Labels: 4 };
    const ingredientsAllAttributesList = ['Ingredients', 'Ingredients 2', 'Ingredients 3'];
    const labels = props.resources;

    // @ts-ignore
    const pTags = [];
    const pSpecifications = [];
    // @ts-ignore
    const ingredientsArray = [];
    let ingredientsString = '';

    const reassuranceItems: ReassuranceShowType[] = props?.slots?.productSpecification?.length
        ? // @ts-ignore
          (props?.slots?.productSpecification[0]?.props?.config?.reassuranceItems ?? []).map(
              (reassurance: IReassuranceItemsExtensionData) => {
                  if (reassurance) {
                      return {
                          title: reassurance.reassuranceTitle,
                          description: reassurance.reassuranceDescription,
                          image: reassurance.reassuranceImage
                      };
                  } else {
                      return undefined;
                  }
              }
          )
        : [];

    const isProductAccordionOpened = props?.slots?.productSpecification?.length
        ? // @ts-ignore
          props?.slots?.productSpecification[0]?.props?.config?.productDescriptionAccordionOpened ?? false
        : false;

    const [accordionsState, setAccordionsState] = React.useState(isProductAccordionOpened ? 'Productdescription' : '');
    const [fetchingLabels, setFetchingLabels] = React.useState(false);
    const [productLabels, setProductLabels] = React.useState([
        {
            label: labels['product_specification_title_emea_label'] ?? 'EMEA label',
            imgLink: ''
        },
        {
            label: labels['product_specification_title_emea_warnings'] ?? 'EMEA warnings',
            imgLink: ''
        },
        {
            label: labels['product_specification_title_fda_label'] ?? 'FDA label',
            imgLink: ''
        }
    ]);

    productSpecificationResult?.map((row: productSpecificationData) => {
        if (row !== null && specificationsList.includes(row.Name) && row.TextValue !== '') {
            pSpecifications.push({
                name: row.Name,
                data: row,
                order: specificationsListOrder[row.Name as keyof typeof specificationsListOrder]
            });
        }
        if (row !== null && ingredientsAllAttributesList.includes(row.Name) && row.TextValue !== '') {
            ingredientsArray.push(row);
        }
    });
    pSpecifications.push({ name: 'Reassurance', order: specificationsListOrder.Reassurance });
    pSpecifications.push({ name: 'Labels', order: specificationsListOrder.Labels });
    pSpecifications.sort((a, b) => a.order - b.order);
    // @ts-ignore
    const productSku = props.data.product.result?.ItemId;
    // @ts-ignore
    const productPrimaryImageUrl = props.data.product.result?.PrimaryImageUrl;

    const addToCartEventHandler = () => {
        const product = props.data.product.result;
        const quantity = Number((document.getElementsByClassName('quantity-input')[0] as HTMLInputElement).value) || 0;

        if (props.data.productPrice.result?.CustomerContextualPrice && props.data.productPrice.result?.AdjustedPrice) {
            trackAddToCartFromPDP(
                props.context,
                product,
                quantity,
                props.data.productPrice.result!.CustomerContextualPrice!,
                props.data.productPrice.result!.AdjustedPrice!
            );
        }
    };

    const addToWishlistEventHandler = () => {
        if (props.data.productPrice.result?.CustomerContextualPrice && props.data.productPrice.result?.AdjustedPrice) {
            trackAddToWishlistFromPDP(
                props.context,
                props.data.product.result,
                props.data.productPrice.result!.CustomerContextualPrice!,
                props.data.productPrice.result!.AdjustedPrice!
            );
        }
    };

    // TODO: remove this after development
    // Debug product attributes
    React.useEffect(() => {
        console.log('*** product specification data');
        console.log({ productSpecificationData: productSpecificationData });
        const attributes = new Map<string, string | boolean>();
        productSpecificationData?.map((row: productSpecificationData) => {
            const attributeName: string = (row.Name ?? '').replace('\n', '').trim();
            const attributeValue: boolean | string = row.BooleanValue ?? row.TextValue ?? row.IntegerValue ?? '';
            attributes.set(attributeName, attributeValue);
        });
        console.log('*** product attributes');
        console.log({ attributes: attributes });
    }, [productSpecificationData]);

    // TODO: remove this after development
    // Debug product attributes
    React.useEffect(() => {
        console.log('*** badges items ***');
        console.log({ badgesItemsState: badgesItemsState });
    }, [badgesItemsState]);

    // TODO: remove this after development
    // Debug product attributes
    React.useEffect(() => {
        console.log('*** badges tag ***');
        console.log({ badgesTagState: badgesTagState });
    }, [badgesTagState]);

    // TODO: remove this after development
    // Debug product attributes
    React.useEffect(() => {
        console.log('*** badges to show ***');
        console.log({ badgesToShowState: badgesToShowState });
    }, [badgesToShowState]);

    React.useEffect(() => {
        const product = props.data.product.result;

        if (props.data.productPrice.result?.CustomerContextualPrice && props.data.productPrice.result?.AdjustedPrice) {
            trackViewItemFromPDP(
                props.context,
                product,
                props.data.productPrice.result!.CustomerContextualPrice!,
                props.data.productPrice.result!.AdjustedPrice!
            );
        }

        document.querySelector('.msc-add-to-wishlist')?.addEventListener('click', addToWishlistEventHandler);
        document.querySelector('.msc-add-to-cart')?.addEventListener('click', addToCartEventHandler);
    }, []);

    // @ts-ignore
    ingredientsArray.map(row => {
        ingredientsString += row.TextValue || '';
    });

    const accordionOnClick = (accordion: string): void => {
        const accStates = accordionsState.split(',');
        if (accStates.includes(accordion)) {
            setAccordionsState('');
        } else {
            setAccordionsState(accordion);
        }
    };

    const isAccordionExpanded = (accordion: string): boolean => {
        const accStates = accordionsState.split(',');
        return accStates.includes(accordion);
    };

    const normalizeData = (data: NutritionalData[]): NutritionalData[] => {
        const resultData: NutritionalData[] = [];
        data.map((row: NutritionalData) => {
            if (row.qty1Value) {
                let category: NutritionalData | undefined;
                const subcategory: NutritionalSubcategoryData[] = [];
                if (row.subcategory) {
                    row.subcategory.map((subRow: NutritionalSubcategoryData) => {
                        if (subRow.qty1Value) {
                            subcategory.push(subRow);
                        }
                    });
                }
                category = {
                    ...row
                };
                if (subcategory.length) {
                    category.subcategory = subcategory;
                } else if (category.subcategory) {
                    // @ts-ignore
                    delete category.subcategory;
                }
                resultData.push(category);
            }
        });
        return resultData;
    };

    const _renderNutritionalValues = (jData: any): JSX.Element | undefined => {
        const normalizedData = normalizeData(jData);
        if (normalizedData.length < 2) {
            return undefined;
        }
        return (
            <div className='nutritional-values-container'>
                {normalizedData.map((row: NutritionalData) => (
                    <>
                        <div className='nutritional-values-row'>
                            {/* @ts-ignore */}
                            <div className='nutritional-values-col'>{labels[row.label] ?? ''}</div>
                            <div className='nutritional-values-col -spacer -line'></div>
                            {/* @ts-ignore */}
                            <div className='nutritional-values-col -pr'>{labels[row.qty1Label] ?? ''}</div>
                            <div className='nutritional-values-col -pr'>{row.qty1Value ?? ''}</div>
                            <div className='nutritional-values-col'>{row.qty1Unit ?? ''}</div>
                        </div>
                        {row.qty2Value && (
                            <div className='nutritional-values-row'>
                                <div className='nutritional-values-col'></div>
                                <div className='nutritional-values-col -spacer'></div>
                                <div className='nutritional-values-col -pr'></div>
                                <div className='nutritional-values-col -pr'>{row.qty2Value ?? ''}</div>
                                <div className='nutritional-values-col'>{row.qty2Unit ?? ''}</div>
                            </div>
                        )}
                        {row.subcategory &&
                            row.subcategory.map((subRow: NutritionalSubcategoryData) => (
                                <div className='nutritional-values-row -subcategory'>
                                    {/* @ts-ignore */}
                                    <div className='nutritional-values-col -subcategory'>{labels[subRow.label] ?? ''}</div>
                                    <div className='nutritional-values-col -spacer'></div>
                                    <div className='nutritional-values-col -subcategory'>{subRow.qty1Value ?? ''}</div>
                                    <div className='nutritional-values-col -subcategory'>{subRow.qty1Unit ?? ''}</div>
                                </div>
                            ))}
                    </>
                ))}
            </div>
        );
    };

    const _renderProductLabels = (productLabels: ProductLabel[]): JSX.Element => {
        return (
            <div className='labels-container'>
                {productLabels
                    .filter(label => label.imgLink !== '')
                    .map((row: ProductLabel) => (
                        <div className='label-row'>
                            <div className='label-col'>
                                <a href={row.imgLink} target='_blank' rel='noreferrer'>
                                    {row.label}
                                </a>
                            </div>
                        </div>
                    ))}
            </div>
        );
    };

    const _renderReassurance = (reassuranceItems: ReassuranceShowType[]): JSX.Element => {
        return (
            <div className='reassurance-container'>
                {reassuranceItems.map((row: ReassuranceShowType, index: number) => {
                    const isLast = index === reassuranceItems.length - 1;
                    const imgSettings = row?.image?.imageSettings ?? undefined;
                    return imgSettings ? (
                        <div className={`reassurance-row${!isLast ? ' -border' : ''}`}>
                            <div className='reassurance-col -image'>
                                <Image
                                    {...row.image}
                                    fallBackSrc=''
                                    altText=''
                                    gridSettings={props.context.request.gridSettings!}
                                    imageSettings={imgSettings}
                                    loadFailureBehavior='empty'
                                />
                            </div>
                            <div className='reassurance-col -text'>
                                <span className='title'>{row.title}</span>
                                <span className='description'>{row.description}</span>
                            </div>
                        </div>
                    ) : (
                        undefined
                    );
                })}
            </div>
        );
    };

    React.useEffect(() => {
        if (!fetchingLabels) {
            setFetchingLabels(true);
            _getProductLabels(productSku, productPrimaryImageUrl).then((response: string[]) => {
                const newProductLabels = productLabels.slice();
                productLabels.forEach((_, index: number) => {
                    newProductLabels[index].imgLink = response[index];
                });
                setProductLabels(newProductLabels);
            });
        }
    });

    // @ts-ignore
    const productShortDescription = props?.data?.productSpecificationData?.result?.filter(
        (productDesc: { Name: string }) => productDesc.Name === 'Short product description'
    )[0]?.TextValue;

    // @ts-ignore
    const isCloseToExpiration = props?.data?.productSpecificationData?.result?.filter((productDesc: { Name: string }) =>
        productDesc.Name.includes('Close To Expiration')
    )[0]?.BooleanValue;

    // @ts-ignore
    const piecesPerPackage = props?.data?.productSpecificationData?.result?.filter(
        (productDesc: { Name: string }) => productDesc.Name === 'Pieces per package'
    )[0]?.IntegerValue;

    // @ts-ignore
    const boxSize = props?.data?.productSpecificationData?.result?.filter(
        (productDesc: { Name: string }) => productDesc.Name === 'Box Size'
    )[0]?.TextValue;

    // @ts-ignore
    const finishedProductPackaging = props?.data?.productSpecificationData?.result?.filter(
        (productDesc: { Name: string }) => productDesc.Name === 'Finished product packaging'
    )[0]?.TextValue;

    const shouldShowBoxiSize =
        boxSize !== undefined &&
        boxSize !== null &&
        boxSize !== '' &&
        finishedProductPackaging !== FinishedProductPackagingEnum.NotRelevant &&
        finishedProductPackaging !== FinishedProductPackagingEnum.EasterMiniEggs &&
        finishedProductPackaging !== FinishedProductPackagingEnum.WrappedChocolate;

    return (
        <Module {...ModuleProps}>
            <div className='left-container'>
                <div className='wishlist-mobile'>{_renderWishlist(addToWishlist)}</div>
                <div className='media-gallery-container'>
                    <Node {...MediaGalleryContainerProps}>{props.mediaGallery}</Node>
                </div>
            </div>
            <div className='right-container'>
                <Node {...ProductInfoContainerProps} className={`availibility__${availibilityCode} ${ProductInfoContainerProps.className}`}>
                    {title}
                    <div className='rendered-price'>{_renderPrice(props, resources, availibilityCode)}</div>
                    {config.freeShippingLabel && <p className='free-shipping-text'>{config.freeShippingLabel}</p>}
                    {availibilityCode && <div className={'ms-buybox__availibility_row'}>{inventoryLabel}</div>}
                    {configure && _renderConfigure(configure)}
                    {keyInPrice && _renderKeyInPrice(keyInPrice)}
                    <Node className='vnc-quantity'>
                        <div className='vcn-addCart'>
                            {quantity &&
                                _renderQuantity(
                                    quantity,
                                    callbacks,
                                    props,
                                    state,
                                    resources,
                                    quantityLimitsMessages,
                                    max,
                                    telemetryContent
                                )}
                            {_renderCartAndActions(addToCart, addToOrderTemplate, props.resources)}
                        </div>
                        <div className='wishlist-desktop'>{_renderWishlist(addToWishlist)}</div>
                        <div className='error-block'>{_renderAddToCartErrorBlock(addToCart)}</div>
                        {/* NOTE Commented the "Add to MyVenchiMix" button on PDP to facilitate A-B tests on the page
                            NOTE ref. https://dev.azure.com/venchi/Venchi.DigitalProducts/_workitems/edit/9624
                            NOTE uncomment whenever requested */}
                        {/* {_renderCartAndActionsPM(addToCart, addToWishlist, props.resources)} */}
                    </Node>

                    <div className={'ms-buybox__product-description'}>
                        {productShortDescription && <ProductDescriptionHTML description={productShortDescription} />}
                        {isCloseToExpiration && <div style={{ fontWeight: 500 }}>{resources.expirationDate}</div>}
                        {piecesPerPackage !== undefined && piecesPerPackage !== null && piecesPerPackage > 0 && (
                            <div style={{ fontWeight: 500 }}>
                                {format(resources.itemsPerPack, piecesPerPackage).split(':')[0]}:{' '}
                                <span style={{ fontWeight: 'initial' }}>
                                    {format(resources.itemsPerPack, piecesPerPackage).split(':')[1]}
                                </span>
                            </div>
                        )}
                        {shouldShowBoxiSize && (
                            <div style={{ fontWeight: 500 }}>
                                {format(resources.boxSizeLabel, boxSize).split(':')[0]}:{' '}
                                <span style={{ fontWeight: 'initial' }}>{format(resources.boxSizeLabel, boxSize).split(':')[1]}</span>
                            </div>
                        )}
                    </div>
                    <div className={'msc-product__extra-data'}>
                        <span className={'data-product-id'}>{props.data.product.result?.RecordId}</span>
                        <span className={'data-product-sku'}>{props.data.product.result?.ItemId}</span>
                    </div>
                    {_renderTextBlock(props.slots.textBlocks)}
                </Node>
                <Node className='buybox-product-specification'>
                    {pSpecifications.length ? (
                        <Node className={'specifications_rows'}>
                            {pSpecifications.map(row => {
                                const title = row.name;
                                const titleId = title.replace(/\s+/g, '');
                                // @ts-ignore
                                let titleLabel = labels[`product_specification_title_${title.replace(/\s+/g, '_').toLowerCase()}`] ?? title;
                                const ingredientsFinalString = ingredientsString;

                                let accordionContent;
                                if (title === 'Product description') {
                                    // Merge product description with ingredients
                                    let content = '';
                                    // @ts-ignore
                                    if (row.data && row.data.TextValue) {
                                        // @ts-ignore
                                        content += row.data.TextValue;
                                    }
                                    if (ingredientsFinalString) {
                                        if (content) {
                                            content +=
                                                `<br><br><strong>${props.resources.product_specification_title_ingredients}:</strong><br>` +
                                                ingredientsFinalString;
                                        } else {
                                            content =
                                                `<strong>${props.resources.product_specification_title_ingredients}:</strong><br>` +
                                                ingredientsFinalString;
                                        }
                                    }
                                    accordionContent = content;
                                } else if (title === 'Nutritional Values JSON') {
                                    try {
                                        // @ts-ignore
                                        const jData = JSON.parse(row.data.TextValue);
                                        accordionContent = _renderNutritionalValues(
                                            jData.nutritionalValueJSONType ? jData.nutritionalValueJSONType : jData
                                        );
                                    } catch (e) {
                                        return;
                                    }
                                } else if (title === 'Reassurance' && reassuranceItems.length > 0) {
                                    accordionContent = _renderReassurance(reassuranceItems);
                                } else if (title === 'Labels' && productLabels.filter(label => label.imgLink !== '').length > 0) {
                                    titleLabel = labels['product_specification_title_product_labels'] ?? titleLabel;
                                    accordionContent = _renderProductLabels(productLabels);
                                    // @ts-ignore
                                } else if (row.data && row.data.TextValue) {
                                    // @ts-ignore
                                    accordionContent = row.data.TextValue;
                                }

                                if (!accordionContent) {
                                    return;
                                }

                                return (
                                    // tslint:disable-next-line:jsx-key
                                    <div className={`${titleId} -accordion`}>
                                        <div className='accordion-header'>
                                            <button
                                                type='button'
                                                tabIndex={0}
                                                id={titleId}
                                                aria-label={titleLabel}
                                                onClick={() => accordionOnClick(titleId)}
                                                aria-expanded={isAccordionExpanded(titleId)}
                                            >
                                                <h2>{titleLabel}</h2>
                                                <div
                                                    className={`accordion-button${
                                                        isAccordionExpanded(titleId) ? ' -expanded' : ' -collapsed'
                                                    }`}
                                                ></div>
                                            </button>
                                        </div>
                                        {titleId === 'Ingredients' ? (
                                            <div
                                                id={`${titleId}_content`}
                                                aria-labelledby={titleId}
                                                className={`content${isAccordionExpanded(titleId) ? ' -expanded' : ''}`}
                                                dangerouslySetInnerHTML={{ __html: accordionContent }}
                                            ></div>
                                        ) : (
                                            <div
                                                id={`${titleId}_content`}
                                                aria-labelledby={titleId}
                                                className={`content${isAccordionExpanded(titleId) ? ' -expanded' : ''}`}
                                                {...(title === 'Product description'
                                                    ? { dangerouslySetInnerHTML: { __html: accordionContent } }
                                                    : {})}
                                            >
                                                {title !== 'Product description' ? accordionContent : null}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                            {_renderSocialShare(props.slots && props.slots?.socialShare)}
                        </Node>
                    ) : (
                        ''
                    )}
                    {maxBadges ? (
                        <div className='ms-buybox__badges'>
                            {badgesItemsState
                                ?.filter((badge: BadgeShowType) => badgesToShowState.includes(badge.key))
                                ?.map((badge: BadgeShowType, index: number) => {
                                    if (index <= maxBadges - 1) {
                                        return _renderBadge(badge, props);
                                    }
                                    return '';
                                })}
                        </div>
                    ) : null}
                </Node>
            </div>
        </Module>
    );
};

// tslint:disable-next-line:no-any
const _renderAddToBackInStore = (
    customerId?: string,
    channelId?: number,
    itemid?: string,
    dimensions?: ProductDimension[],
    actionContext?: any,
    resources?: IBuyboxResources & IBuyboxExtensionResources,
    emailAddress?: string,
    locale?: string
): JSX.Element => {
    const cust = customerId ? customerId : '';
    const item = itemid ? itemid : '';
    const channel = channelId ? channelId : 0;
    const dims = dimensions ? dimensions : [];
    let color: string = '';
    let config: string = '';
    let size: string = '';
    let style: string = '';
    // prettier-ignore
    // @ts-ignore
    let message = resources.backInStockMessage ? resources.backInStockMessage : 'You will receive an email when the product is back in stock';
    let toHide = false;

    dims.forEach(dim => {
        if (dim.DimensionTypeValue === 1) {
            color = dim.DimensionValue?.DimensionId !== undefined ? dim.DimensionValue?.DimensionId : '';
        } else if (dim.DimensionTypeValue === 2) {
            config = dim.DimensionValue?.DimensionId !== undefined ? dim.DimensionValue?.DimensionId : '';
        } else if (dim.DimensionTypeValue === 3) {
            size = dim.DimensionValue?.DimensionId !== undefined ? dim.DimensionValue?.DimensionId : '';
        } else if (dim.DimensionTypeValue === 4) {
            style = dim.DimensionValue?.DimensionId !== undefined ? dim.DimensionValue?.DimensionId : '';
        }
    });

    toHide = cust !== '' ? false : true;
    const [stockMessage, setStockMessage] = React.useState(false);

    const _AddToBackInStore = async () => {
        insertBackInStock_CAPAsync_v2(
            { callerContext: actionContext },
            channel,
            cust,
            item,
            color,
            config,
            size,
            style,
            1,
            emailAddress || '',
            locale || ''
        ).then(result => {
            message = result[0].Error ? result[0].Error : message;
        });
        setStockMessage(true);
    };
    const inactiveAccount = cust.includes('-');

    return (
        <>
            {toHide === false && (
                <div className={'ms-buybox__backInStock'}>
                    <button
                        id='backinstock'
                        hidden={toHide}
                        disabled={inactiveAccount}
                        className={'ms-buybox__backinstock-btn'}
                        onClick={_AddToBackInStore}
                    >
                        {inactiveAccount
                            ? resources?.outOfStockText
                                ? resources.outOfStockText
                                : 'THIS ITEM IS OUT OF STOCK'
                            : resources?.notifyMeWhenAvailable !== undefined
                            ? resources.notifyMeWhenAvailable
                            : 'NOTIFY ME WHEN AVAILABLE'}
                    </button>
                    {stockMessage ? (
                        <div className='msc-alert msc-alert-success' role='alert'>
                            <div>
                                {/* tslint:disable-next-line:jsx-no-lambda react-this-binding-issue */}
                                <button type='button' className='close' aria-label='Close' onClick={() => setStockMessage(false)} />
                            </div>
                            {message}
                        </div>
                    ) : (
                        ''
                    )}
                </div>
            )}
        </>
    );
};

const _renderTextBlock = (textBlocks: React.ReactNode[]): JSX.Element | undefined => {
    if (!textBlocks || textBlocks.length === 0) {
        return undefined;
    }

    return <>{textBlocks[0]}</>;
};

const _renderPrice = (
    props: IBuyboxViewProps,
    resources: IBuyboxResources & IBuyboxExtensionResources,
    availabilityCode?: string
): JSX.Element | undefined => {
    const custom_price = props.context.cultureFormatter.formatCurrency(props.data.productPrice.result?.AdjustedPrice?.toFixed(2) ?? '');
    const custom_discount_percentage = getCustomDiscountPercentage(props.data.productPrice.result?.DiscountLines);
    const custom_discounted_price = props.context.cultureFormatter.formatCurrency(
        props.data.productPrice.result?.CustomerContextualPrice?.toFixed(2) ?? ''
    );

    if (custom_discount_percentage) {
        // @ts-ignore
        const attributes = props.data.productSpecificationData.result;

        let omnibus_price = null;
        if (attributes) {
            // @ts-ignore
            omnibus_price = attributes.filter(attribute => attribute.Name.includes('Minimum Price Last 30 Days'))[0];
        }

        let omnibus_price_currency = '';
        if (omnibus_price && omnibus_price.CurrencyValue && omnibus_price.CurrencyCode) {
            omnibus_price_currency = props.context.cultureFormatter.formatCurrency(omnibus_price.CurrencyValue, omnibus_price.CurrencyCode);
        }

        return (
            <>
                <span className='msc-price-discount-container'>
                    <div className='msc-price-discount-prices'>
                        <span className='msc-price__discount'>{custom_discount_percentage}</span>
                        <span className='msc-price__actual'>{custom_discounted_price}</span>
                        <span className='msc-price__strikethrough'>{custom_price}</span>
                    </div>
                    {!availabilityCode && <div className='available-label'>{resources.availableLabel}</div>}
                </span>
                {omnibus_price_currency && (
                    <div className='omnibus-details'>
                        <span className='msc-price__recent-price'>
                            {resources.omnibus_recent_price_label}&nbsp;
                            <span style={{ textDecoration: 'line-through' }}>{omnibus_price_currency}</span>
                        </span>
                    </div>
                )}
            </>
        );
    }
    return (
        <span className='msc-price-container'>
            <span className='msc-price'>{custom_price}</span>
            {!availabilityCode && <div className='available-label'>{resources.availableLabel}</div>}
        </span>
    );
};

// NOTE Commented the "Add to MyVenchiMix" button on PDP to facilitate A-B tests on the page
// NOTE ref. https://dev.azure.com/venchi/Venchi.DigitalProducts/_workitems/edit/9624
// NOTE uncomment whenever requested
// function _addToPickAndMix(
//     catUrl: string,
//     pmProductsList: any,
//     RecordId: number,
//     Name: string | undefined,
//     PrimaryImageUrl: string | undefined,
//     pQuantity: number,
//     Price: number,
//     ItemId: number
// ): void {
//     pmProductsList[RecordId] = {
//         Id: RecordId,
//         Item: Name,
//         Qty: pQuantity,
//         UOM: '',
//         Price: Price,
//         Description: ItemId.toString(),
//         DataAreaId: '',
//         PrimaryImageUrl: PrimaryImageUrl
//     };
//     typeof window !== 'undefined' ? localStorage.setItem('pm_products', JSON.stringify(pmProductsList)) : undefined;
//     window.location.assign(catUrl);
// }

const _renderCartAndActions = (
    addToCart?: IBuyboxAddToCartViewProps,
    addToOrderTemplate?: IBuyboxAddToOrderTemplateViewProps,
    resources?: IBuyboxResources & IBuyboxExtensionResources
): JSX.Element | undefined => {
    if (!addToCart) {
        return undefined;
    }
    // @ts-ignore
    const props = addToCart?.button?.props;

    const siteLanguageConfig = getSiteLanguageConfigFromContext(props.context);
    const locale = determineSiteLanguageForAbandonedCart(siteLanguageConfig);

    return (
        <div className='product-add-to-cart'>
            {addToOrderTemplate && addToOrderTemplate.errorBlock}
            <div className='buttons-wrapper'>
                <div className='buttons'>
                    {_renderAddToBackInStore(
                        props.context.request.user.customerAccountNumber,
                        props.context.request.apiSettings.channelId,
                        props.data?.product?.ItemId,
                        props.data?.product?.Dimensions,
                        props.context?.actionContext,
                        resources,
                        props.context.request.user.emailAddress,
                        locale
                    )}
                    {addToCart && addToCart.button}
                    {addToOrderTemplate && addToOrderTemplate.button}
                </div>
            </div>
            {props.context.request.user.customerAccountNumber !== null && !props.context.request.user.customerAccountNumber.includes('-') && (
                <div className={'privacyForBackInStock'}>
                    {
                        // @ts-ignore
                        resources.backInStockAgreement
                    }
                </div>
            )}
        </div>
    );
};

const _renderWishlist = (addToWishlist?: IBuyboxAddToWishlistViewProps): JSX.Element | undefined => {
    if (!addToWishlist) {
        return undefined;
    }

    return (
        <div className='product-wishlist'>
            {addToWishlist && addToWishlist.errorBlock}
            {addToWishlist && <Node className='vnc-addwishlist'>{addToWishlist && addToWishlist.button}</Node>}
        </div>
    );
};

const _renderAddToCartErrorBlock = (addToCart?: IBuyboxAddToCartViewProps): JSX.Element | undefined => {
    if (!addToCart) {
        return undefined;
    }

    return <>{addToCart && addToCart.errorBlock}</>;
};

// NOTE Commented the "Add to MyVenchiMix" button on PDP to facilitate A-B tests on the page
// NOTE ref. https://dev.azure.com/venchi/Venchi.DigitalProducts/_workitems/edit/9624
// NOTE uncomment whenever requested
// const _renderCartAndActionsPM = (
//     addToCart?: IBuyboxAddToCartViewProps,
//     addToWishlist?: IBuyboxAddToWishlistViewProps,
//     resources?: IBuyboxResources & IBuyboxExtensionResources
// ): JSX.Element | undefined => {
//     if (!addToCart && !addToWishlist) {
//         return undefined;
//     }
//     // @ts-ignore
//     const props = addToCart?.button?.props;
//     const productId = props?.data?.product?.RecordId;
//     const channelId = props?.context?.request?.channel?.RecordId;
//     const categoryPathLookups = [{ ProductId: productId }];
//     const [catUrl, setCatUrl] = React.useState('');
//     const [isPm, setIsPm] = React.useState(false);
//     const _productCategory = async () => {
//         const categoryPathResults = await getCategoryPathsAsync(props?.context?.actionContext, channelId, 0, categoryPathLookups);
//         categoryPathResults &&
//             categoryPathResults[0] &&
//             categoryPathResults[0]?.CategoryPath?.map((catPath, i) => {
//                 const categoryUrl = getCategoriesUrlSync(catPath, props?.context?.actionContext);
//                 if (categoryUrl && categoryUrl[1] && categoryUrl[1].Name === 'Pick&Mix') {
//                     setIsPm(true);
//                     setCatUrl(categoryUrl[1].Url ? categoryUrl[1].Url : '');
//                 }
//             });
//         return;
//     };
//     React.useEffect(() => {
//         void _productCategory();
//     }, []);
//     // tslint:disable-next-line:no-shadowed-variable
//     const _addToPM = (
//         catUrl: string,
//         RecordId: number,
//         Name: string,
//         PrimaryImageUrl: string,
//         AdjustedPrice: number,
//         Qty: number,
//         ItemId: number
//     ) => {
//         const localPm = localStorage && localStorage.getItem('pm_products');
//         const localPmProds = localPm && localPm !== null ? localPm : JSON.stringify({});
//         const pmProductsList = JSON.parse(localPmProds);
//         const _addToPm = () => {
//             _addToPickAndMix(catUrl, pmProductsList, RecordId, Name, PrimaryImageUrl, AdjustedPrice, Qty, ItemId);
//         };
//         return (
//             <>
//                 <button className={'border-button msc-add-to-pm'} onClick={_addToPm}>
//                     {// @ts-ignore
//                     resources.addToPickAndMix ? resources.addToPickAndMix : 'ADD TO PICK AND MIX'}
//                 </button>
//             </>
//         );
//     };
//     /* @ts-ignore */
//     const product_qty: product_qty = addToCart?.button?.props?.quantity;
//     return (
//         <div className='product-add-to-cart-pm'>
//             {isPm ? (
//                 <div className='buttons'>
//                     {_addToPM(
//                         catUrl,
//                         props.data?.product?.RecordId,
//                         props.data?.product?.Name,
//                         props.data?.product?.PrimaryImageUrl,
//                         product_qty,
//                         props.data?.product?.AdjustedPrice,
//                         props.data?.product?.ItemId
//                     )}
//                 </div>
//             ) : (
//                 ''
//             )}
//         </div>
//     );
// };

const _renderConfigure = (configure: IBuyboxProductConfigureViewProps): JSX.Element => {
    const { ContainerProps, dropdowns } = configure;

    return <Node {...ContainerProps}>{dropdowns.map(_renderConfigureDropdown)}</Node>;
};

const _renderConfigureDropdown = (dropdown: IBuyboxProductConfigureDropdownViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, errors, select } = dropdown;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>
                {heading}
                {errors}
            </Node>
            {select}
        </Node>
    );
};

const _renderQuantity = (
    quantityComponent: IBuyboxProductQuantityViewProps,
    callbacks: IBuyboxCallbacks,
    props: IBuyboxProps<IBuyboxData>,
    state: IBuyboxState,
    extentionResources: IBuyboxResources & IBuyboxExtensionResources,
    quantityLimitsMessages: React.ReactNode,
    max: number | undefined,
    telemetryContent?: ITelemetryContent
): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, errors } = quantityComponent;

    const { resources } = props;

    const { quantity } = state;

    if (state.isCustomPriceSelected) {
        props.data;
    }
    const onChange = (newValue: number): boolean => {
        if (callbacks.updateQuantity) {
            return callbacks.updateQuantity(newValue);
        }
        return true;
    };

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>
                {heading}
                {errors}
            </Node>

            <Quantity
                id='ms-buybox__product-quantity-input'
                max={max}
                currentCount={quantity}
                onChange={onChange}
                inputQuantityAriaLabel={resources.inputQuantityAriaLabel}
                decrementButtonAriaLabel={extentionResources.decrementButtonAriaLabel}
                incrementButtonAriaLabel={extentionResources.incrementButtonAriaLabel}
                minQuantityText={extentionResources.minQuantityText}
                maxQuantityText={extentionResources.maxQuantityText}
                telemetryContent={telemetryContent}
            />
            {quantityLimitsMessages}
        </Node>
    );
};

const _renderKeyInPrice = (keyInPrice: IBuyboxKeyInPriceViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, input } = keyInPrice;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>{heading}</Node>
            {input}
        </Node>
    );
};

const _renderBadge = (badge: BadgeShowType, props: IBuyboxViewProps) => {
    const imgSettings = badge?.image?.imageSettings ?? undefined;
    if (!imgSettings) {
        return undefined;
    }
    return (
        <div className='badge'>
            <Image
                {...badge.image}
                fallBackSrc=''
                altText=''
                gridSettings={props.context.request.gridSettings!}
                imageSettings={imgSettings}
                loadFailureBehavior='empty'
            />
            <span className={badge.color}>{badge.label}</span>
        </div>
    );
};

const _getProductLabels = async (productId?: string, baseImagePath?: string): Promise<string[]> => {
    if (productId && baseImagePath) {
        const results: string[] = [];
        const imgBaseUrlArray = decodeURIComponent(baseImagePath).split('/');
        imgBaseUrlArray.pop();

        const imgUrls = [
            imgBaseUrlArray.concat([`${productId}_000_006.jpg`]).join('/'),
            imgBaseUrlArray.concat([`${productId}_000_007.jpg`]).join('/'),
            imgBaseUrlArray.concat([`${productId}_000_008.jpg`]).join('/')
        ];
        for (let i = 0; i < 3; i = i + 1) {
            try {
                const response = await axios.get(imgUrls[i]);
                results.push(response?.status === 200 ? imgUrls[i] : '');
            } catch (e) {
                results.push('');
            }
        }
        return results;
    }
    return ['', '', ''];
};

const _renderSocialShare = (socialShare: React.ReactNode[]): JSX.Element | undefined => {
    if (!socialShare || socialShare.length === 0) {
        return undefined;
    }
    return <>{socialShare[0]}</>;
};

export default BuyboxView;
